import { Droppable } from "@hello-pangea/dnd";
import React from "react";

import { AddReleaseLabelTrigger } from "../AddReleaseLabelButton";
import {
  PendingDeletesByLabelId,
  PendingReleaseLabelMovesRegistry,
} from "../hooks/usePendingReleaseLabelMoves";

import DraggableReleaseLabel from "./DraggableReleaseLabel";
import DynamicReleaseLabel from "./DynamicReleaseLabel";
import PendingReleaseLabel from "./PendingReleaseLabel";
import { Label } from "./types";

import type { VersionSelectorVersionId } from "@/components/VersionSelector/types";

interface DroppableVersionLabelsProps {
  versionId: VersionSelectorVersionId;
  versionNumber: number;
  labels: Label[];
  dynamicReleaseLabels: Label[];
  handleDynamicReleaseLabelClick: (id: number) => void;
  isActiveVersion: boolean;
  onOpenAddLabelDialog: () => void;
  onConfigureLabel?: (label: Label) => void;
  hasProtectedLabels: boolean;
  pendingReleaseLabelMoves?: PendingReleaseLabelMovesRegistry;
  pendingDeletesByLabelId?: PendingDeletesByLabelId;
  onPendingLabelClick?: (changelogId: number) => void;
  cannotDeployReleaseLabels: boolean;
}

const DroppableVersionLabels = ({
  versionId,
  versionNumber,
  labels,
  dynamicReleaseLabels,
  handleDynamicReleaseLabelClick,
  isActiveVersion,
  onOpenAddLabelDialog,
  onConfigureLabel,
  hasProtectedLabels,
  pendingReleaseLabelMoves,
  pendingDeletesByLabelId,
  onPendingLabelClick,
  cannotDeployReleaseLabels,
}: DroppableVersionLabelsProps) => {
  // Get pending label moves for this version
  const pendingMovesForVersion = pendingReleaseLabelMoves?.[versionNumber];
  const incomingPendingLabels = pendingMovesForVersion?.incoming ?? [];

  return (
    <div className="ml-6 flex items-center justify-between">
      <Droppable
        isDropDisabled={cannotDeployReleaseLabels && !hasProtectedLabels}
        droppableId={`version-${versionId}`}
        direction="horizontal"
        type="RELEASE_LABEL"
      >
        {(provided, snapshot) => (
          <div
            {...provided.droppableProps}
            ref={provided.innerRef}
            className={`flex w-auto max-w-full flex-wrap gap-1 py-0.5 text-xs ${
              snapshot.isDraggingOver ? "rounded-sm bg-blue-50" : ""
            }`}
          >
            {dynamicReleaseLabels.map(label => (
              <DynamicReleaseLabel
                key={label.name}
                label={label}
                onClick={handleDynamicReleaseLabelClick}
              />
            ))}
            {labels.map((labelItem, index) => (
              <DraggableReleaseLabel
                readOnly={cannotDeployReleaseLabels && !labelItem.is_protected}
                key={`${labelItem.name}-${labelItem.id}`}
                label={labelItem}
                index={index}
                isActiveVersion={isActiveVersion}
                onConfigure={onConfigureLabel}
                pendingDeleteChangelogId={pendingDeletesByLabelId?.get(labelItem.id)?.changelogId}
                onPendingDeleteClick={onPendingLabelClick}
              />
            ))}
            {incomingPendingLabels.map(pendingMove => (
              <div
                key={`pending-${pendingMove.changelogId}`}
                onClick={e => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
              >
                <PendingReleaseLabel
                  labelName={pendingMove.labelName}
                  labelId={pendingMove.labelId}
                  changelogId={pendingMove.changelogId}
                  direction="incoming"
                  fromVersionNumber={pendingMove.fromVersionNumber}
                  toVersionNumber={pendingMove.toVersionNumber}
                  onClick={onPendingLabelClick ?? (() => undefined)}
                />
              </div>
            ))}
            {provided.placeholder}
            <div
              onClick={e => {
                e.preventDefault();
                e.stopPropagation();
              }}
              className={`${labels.length === 0 ? "opacity-50 group-hover:opacity-100" : ""}`}
            >
              <AddReleaseLabelTrigger
                isEmpty={labels.length === 0}
                onOpenDialog={onOpenAddLabelDialog}
                readOnly={cannotDeployReleaseLabels}
                tooltipContent={
                  cannotDeployReleaseLabels
                    ? "You don't have permission to add release labels"
                    : undefined
                }
              />
            </div>
          </div>
        )}
      </Droppable>
    </div>
  );
};

export default DroppableVersionLabels;
